import React from 'react';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import MovieIcon from '@mui/icons-material/Movie';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import TableChartIcon from '@mui/icons-material/TableChart';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AttachFileIcon from '@mui/icons-material/AttachFile';

const CODE_EXTENSIONS = new Set([
  'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'c', 'cpp', 'h', 'hpp',
  'cs', 'rb', 'go', 'rs', 'swift', 'kt', 'scala', 'php', 'r',
  'sql', 'sh', 'bash', 'zsh', 'ps1', 'bat', 'cmd',
  'html', 'css', 'scss', 'sass', 'less', 'xml', 'svg',
  'json', 'yaml', 'yml', 'toml', 'ini', 'cfg', 'conf',
  'md', 'txt', 'csv', 'log', 'env', 'gitignore', 'dockerfile',
]);

const LANGUAGE_MAP = {
  js: 'javascript', jsx: 'jsx', ts: 'typescript', tsx: 'tsx',
  py: 'python', java: 'java', c: 'c', cpp: 'cpp', h: 'c', hpp: 'cpp',
  cs: 'csharp', rb: 'ruby', go: 'go', rs: 'rust', swift: 'swift',
  kt: 'kotlin', scala: 'scala', php: 'php', r: 'r',
  sql: 'sql', sh: 'bash', bash: 'bash', zsh: 'bash',
  html: 'html', css: 'css', scss: 'scss', sass: 'sass', less: 'less',
  xml: 'xml', svg: 'xml', json: 'json', yaml: 'yaml', yml: 'yaml',
  toml: 'toml', md: 'markdown', txt: 'text', csv: 'text',
  ini: 'ini', cfg: 'text', conf: 'text', log: 'text',
  dockerfile: 'docker', env: 'text', gitignore: 'text',
};

function getExtension(filename) {
  if (!filename) return '';
  return filename.split('.').pop().toLowerCase();
}

function isTextMimeType(mimeType) {
  if (!mimeType) return false;
  return (
    mimeType.startsWith('text/') ||
    mimeType === 'application/json' ||
    mimeType === 'application/xml' ||
    mimeType === 'application/javascript' ||
    mimeType === 'application/x-yaml' ||
    mimeType === 'application/x-sh'
  );
}

export function isTextFile(mimeType, filename) {
  if (isTextMimeType(mimeType)) return true;
  return CODE_EXTENSIONS.has(getExtension(filename));
}

export function getPreviewType(mimeType, filename) {
  if (!mimeType && !filename) return 'unknown';
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType && mimeType.startsWith('image/')) return 'image';
  if (mimeType && mimeType.startsWith('video/')) return 'video';
  if (mimeType && mimeType.startsWith('audio/')) return 'audio';
  if (isTextFile(mimeType, filename)) return 'text';
  return 'unknown';
}

export function canPreviewFile(mimeType, filename) {
  return getPreviewType(mimeType, filename) !== 'unknown';
}

export function getLanguageFromFilename(filename) {
  const ext = getExtension(filename);
  return LANGUAGE_MAP[ext] || 'text';
}

export function parseFileUrlFromHref(href) {
  if (!href) return null;

  // Pattern: /api/courses/{courseId}/files/{fileId}/download/
  const downloadMatch = href.match(/\/api\/courses\/(\d+)\/files\/(\d+)\/download\/?/);
  if (downloadMatch) {
    return { courseId: parseInt(downloadMatch[1], 10), fileId: parseInt(downloadMatch[2], 10) };
  }

  // Pattern: /media/course_files/{courseId}/
  const mediaMatch = href.match(/\/media\/course_files\/(\d+)\//);
  if (mediaMatch) {
    return { courseId: parseInt(mediaMatch[1], 10), fileId: null, mediaUrl: href };
  }

  return null;
}

export function formatFileSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function getFileIcon(mimeType, size = 24) {
  if (!mimeType) return <AttachFileIcon sx={{ fontSize: size }} />;
  if (mimeType === 'application/pdf') return <PictureAsPdfIcon sx={{ fontSize: size, color: 'error.light' }} />;
  if (mimeType.startsWith('image/')) return <ImageIcon sx={{ fontSize: size, color: 'info.light' }} />;
  if (mimeType.startsWith('video/')) return <MovieIcon sx={{ fontSize: size, color: 'secondary.light' }} />;
  if (mimeType.startsWith('audio/')) return <AudioFileIcon sx={{ fontSize: size, color: 'warning.light' }} />;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return <TableChartIcon sx={{ fontSize: size, color: 'success.light' }} />;
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return <SlideshowIcon sx={{ fontSize: size, color: 'warning.main' }} />;
  if (mimeType.includes('word') || mimeType.includes('document')) return <DescriptionIcon sx={{ fontSize: size, color: 'info.main' }} />;
  if (mimeType.includes('zip') || mimeType.includes('compressed')) return <FolderZipIcon sx={{ fontSize: size, color: 'text.secondary' }} />;
  return <InsertDriveFileIcon sx={{ fontSize: size, color: 'text.secondary' }} />;
}

// Max file size for text preview (2MB)
export const TEXT_PREVIEW_MAX_SIZE = 2 * 1024 * 1024;
